import { notifyApiError, notifyApiSuccess } from "./apiNotify";
import {
  updateApplicationData,
  updateApplication,
  safePdf,
  postApplication,
  getApplication,
  isThunkFulfilled,
} from "../store/reducers/applicationSlice";

async function readApplicationState(id, token) {
  const app = await getApplication(id, token);
  return app?.state ?? app?.application?.state ?? null;
}

/**
 * Change application status after data/PDF are saved.
 * Prefers /updateApplication (reliable for company status transitions),
 * then falls back to PUT /:id, and verifies the persisted state.
 */
export async function updateApplicationStatus(dispatch, {
  id,
  token,
  userId,
  json_data,
  form,
  nextState,
}) {
  if (!nextState) {
    return { ok: false, result: null };
  }

  // 1) Primary: multipart updateApplication with new state (same as in-review submit)
  const dataRes = await dispatch(
    updateApplicationData({
      id,
      token,
      userId,
      ...(json_data !== undefined ? { json_data } : {}),
      form,
      state: nextState,
      silent: true,
    })
  );

  if (isThunkFulfilled(dataRes)) {
    const fromPayload = dataRes?.payload?.state;
    if (fromPayload === nextState) {
      return { ok: true, result: dataRes };
    }
    const verified = await readApplicationState(id, token);
    if (verified === nextState) {
      return { ok: true, result: dataRes };
    }
  }

  // 2) Fallback: PUT /applications/:id (not status-locked)
  const putRes = await dispatch(
    updateApplication({
      id,
      token,
      state: nextState,
      silent: true,
    })
  );

  if (isThunkFulfilled(putRes)) {
    const fromPayload = putRes?.payload?.state;
    if (fromPayload === nextState) {
      return { ok: true, result: putRes };
    }
    const verified = await readApplicationState(id, token);
    if (verified === nextState) {
      return { ok: true, result: putRes };
    }
  }

  const actual = await readApplicationState(id, token);
  notifyApiError(
    putRes?.payload || dataRes?.payload,
    actual
      ? `Status is still "${actual}" (expected ${nextState})`
      : "Failed to update application status"
  );
  return {
    ok: false,
    result: putRes || dataRes,
    actualState: actual,
  };
}

/**
 * Required sequence for existing applications:
 * 1) Save application data WITHOUT changing status
 * 2) Save PDF (only if step 1 succeeded)
 * 3) Update status (only if step 2 succeeded and nextState is provided)
 *
 * Success toast is shown only when all requested steps succeed.
 */
export async function saveDataPdfThenStatus(dispatch, options = {}) {
  const {
    id,
    token,
    userId,
    json_data,
    files,
    form,
    currentState,
    nextState,
    htmlForm,
    pdfUser,
    successMessage,
  } = options;

  if (!id || !token) {
    notifyApiError(null, "Missing application id or token");
    return { ok: false, step: "save" };
  }

  // 1) Save data — keep status unchanged (omit state unless we know the current value)
  const savePayload = {
    id,
    token,
    userId,
    json_data,
    files,
    form,
    silent: true,
  };
  if (currentState != null && currentState !== "") {
    savePayload.state = currentState;
  }

  const saveRes = await dispatch(updateApplicationData(savePayload));

  if (!isThunkFulfilled(saveRes)) {
    notifyApiError(saveRes.payload, "Failed to save application");
    return { ok: false, step: "save", result: saveRes };
  }

  // 2) PDF — stop here on failure so status stays original
  if (htmlForm) {
    const pdfRes = await dispatch(
      safePdf({
        id,
        token,
        htmlForm,
        user: pdfUser,
        silent: true,
      })
    );

    if (!isThunkFulfilled(pdfRes)) {
      notifyApiError(pdfRes.payload, "Failed to save PDF");
      return { ok: false, step: "pdf", result: pdfRes };
    }
  }

  // 3) Status change only after PDF success
  if (nextState != null && String(nextState) !== String(currentState)) {
    const statusResult = await updateApplicationStatus(dispatch, {
      id,
      token,
      userId,
      json_data,
      form,
      nextState,
    });

    if (!statusResult.ok) {
      return { ok: false, step: "status", result: statusResult.result };
    }
  }

  if (successMessage) {
    notifyApiSuccess(successMessage);
  }

  return { ok: true };
}

/**
 * First-time create flow:
 * 1) Create as drafts (status that still allows PDF)
 * 2) Save PDF
 * 3) Move to final status
 */
export async function createDraftPdfThenStatus(dispatch, options = {}) {
  const {
    token,
    user_id,
    json_data,
    files,
    form,
    name,
    category,
    nextState,
    htmlForm,
    pdfUser,
    successMessage,
  } = options;

  const createRes = await dispatch(
    postApplication({
      form,
      name,
      category,
      json_data,
      files,
      user_id,
      token,
      state: "drafts",
      silent: true,
    })
  );

  if (!isThunkFulfilled(createRes) || !createRes?.payload?.id) {
    notifyApiError(createRes.payload || createRes.error, "Failed to create application");
    return { ok: false, step: "create", result: createRes };
  }

  const applicationId = createRes.payload.id;

  if (htmlForm) {
    const pdfRes = await dispatch(
      safePdf({
        id: applicationId,
        token,
        htmlForm,
        user: pdfUser || { id: user_id },
        silent: true,
      })
    );

    if (!isThunkFulfilled(pdfRes)) {
      notifyApiError(pdfRes.payload, "Failed to save PDF");
      return { ok: false, step: "pdf", applicationId, result: pdfRes };
    }
  }

  if (nextState && nextState !== "drafts") {
    const statusResult = await updateApplicationStatus(dispatch, {
      id: applicationId,
      token,
      userId: user_id,
      json_data,
      form,
      nextState,
    });

    if (!statusResult.ok) {
      return {
        ok: false,
        step: "status",
        applicationId,
        result: statusResult.result,
      };
    }
  }

  if (successMessage) {
    notifyApiSuccess(successMessage);
  }

  return { ok: true, applicationId };
}
